import { supabase } from "@/platform/lib/supabase";
import { scheduleUspReviews } from "@/tenants/usp/features/review/api";
import { fetchQuestionPageImages } from "@/tenants/usp/lib/page-image";

/**
 * Simulador 1ª fase Fuvest — 90 questões objetivas em 5h.
 * Persiste em usp_sessions (kind='simulation_1f') + usp_attempts.
 *
 * Por hora o pool tem 270 questões (3 anos). Sorteio aleatório com
 * distribuição que aproxima da distribuição real Fuvest 1ª fase
 * (geo 13, fis 11, mat 10, etc).
 */

export interface SimQuestion {
  id: string;
  year: number;
  number: number;
  discipline: string;
  stem: string;
  has_base_text: boolean;
  base_text: string | null;
  alternatives: { A: string; B: string; C: string; D: string; E: string };
  correct_answer: string | null;
  has_image?: boolean;
  has_graph?: boolean;
  has_table?: boolean;
  exam_paper_id?: string | null;
  page_number?: number | null;
  pageImageUrl?: string;
}

const SIM_SIZE = 90;
export const SIM_DURATION_SECONDS = 5 * 60 * 60; // 5h

// Distribuição alvo da 1ª fase Fuvest (baseada no corpus 268 obj)
const TARGET_DISTRIBUTION: Record<string, number> = {
  geografia: 13,
  fisica: 11,
  matematica: 10,
  quimica: 10,
  historia: 9,
  biologia: 8,
  literatura: 8,
  ingles: 7,
  sociologia: 5,
  interdisciplinar: 4,
  portugues: 4,
  filosofia: 2,
  artes: 2,
};

export async function startSimulationSession(userId: string): Promise<{
  sessionId: string;
  questions: SimQuestion[];
  startedAt: string;
}> {
  // 1. Pool de questões classificadas com gabarito
  const { data: pool, error: poolErr } = await supabase
    .from("usp_questions")
    .select(
      "id, year, number, discipline, stem, has_base_text, base_text, alternatives, correct_answer, has_image, has_graph, has_table, exam_paper_id, page_number",
    )
    .eq("kind", "objective")
    .eq("phase", "1")
    .not("correct_answer", "is", null);
  if (poolErr || !pool) throw new Error(`load pool: ${poolErr?.message}`);

  // 2. Stratified sampling pelo target
  const byDisc = new Map<string, SimQuestion[]>();
  for (const q of pool as SimQuestion[]) {
    const arr = byDisc.get(q.discipline) ?? [];
    arr.push(q);
    byDisc.set(q.discipline, arr);
  }
  for (const arr of byDisc.values()) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j]!, arr[i]!];
    }
  }

  const picked: SimQuestion[] = [];
  for (const [disc, target] of Object.entries(TARGET_DISTRIBUTION)) {
    const bucket = byDisc.get(disc) ?? [];
    picked.push(...bucket.slice(0, target));
  }
  // Se faltou (pool < target), preencher com aleatório do total
  if (picked.length < SIM_SIZE) {
    const used = new Set(picked.map((q) => q.id));
    const extras = (pool as SimQuestion[]).filter((q) => !used.has(q.id));
    for (let i = extras.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [extras[i], extras[j]] = [extras[j]!, extras[i]!];
    }
    picked.push(...extras.slice(0, SIM_SIZE - picked.length));
  }
  // shuffle final order
  for (let i = picked.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [picked[i], picked[j]] = [picked[j]!, picked[i]!];
  }
  const finalQs = picked.slice(0, SIM_SIZE);

  const images = await fetchQuestionPageImages(finalQs);
  for (const q of finalQs) if (images[q.id]) q.pageImageUrl = images[q.id];

  const startedAt = new Date().toISOString();

  // 3. Cria sessão
  const { data: sess, error: sessErr } = await supabase
    .from("usp_sessions")
    .insert({
      user_id: userId,
      kind: "simulation_1f",
      started_at: startedAt,
      total_questions: finalQs.length,
      metadata: { question_ids: finalQs.map((q) => q.id) },
    })
    .select("id")
    .single();
  if (sessErr || !sess) throw new Error(`create session: ${sessErr?.message}`);

  return {
    sessionId: sess.id as string,
    questions: finalQs,
    startedAt,
  };
}

export async function loadSimulationSession(sessionId: string): Promise<{
  sessionId: string;
  questions: SimQuestion[];
  startedAt: string;
  finishedAt: string | null;
}> {
  const { data: sess, error } = await supabase
    .from("usp_sessions")
    .select("id, started_at, finished_at, metadata")
    .eq("id", sessionId)
    .single();
  if (error || !sess) throw new Error(`session not found: ${error?.message}`);

  const ids = (sess.metadata as { question_ids?: string[] } | null)?.question_ids ?? [];
  const { data: qs, error: qErr } = await supabase
    .from("usp_questions")
    .select(
      "id, year, number, discipline, stem, has_base_text, base_text, alternatives, correct_answer, has_image, has_graph, has_table, exam_paper_id, page_number",
    )
    .in("id", ids);
  if (qErr || !qs) throw new Error(`load questions: ${qErr?.message}`);

  const byId = new Map((qs as SimQuestion[]).map((q) => [q.id, q]));
  const questions = ids.map((id) => byId.get(id)).filter((q): q is SimQuestion => Boolean(q));

  const images = await fetchQuestionPageImages(questions);
  for (const q of questions) if (images[q.id]) q.pageImageUrl = images[q.id];

  return {
    sessionId: sess.id as string,
    questions,
    startedAt: sess.started_at as string,
    finishedAt: sess.finished_at as string | null,
  };
}

export async function recordSimAttempt(params: {
  userId: string;
  sessionId: string;
  questionId: string;
  selected: string;
  correctAnswer: string | null;
  timeSeconds: number;
}): Promise<void> {
  const isCorrect = params.correctAnswer ? params.selected === params.correctAnswer : null;
  const { error } = await supabase.from("usp_attempts").insert({
    user_id: params.userId,
    session_id: params.sessionId,
    question_id: params.questionId,
    answer: params.selected,
    is_correct: isCorrect,
    score: isCorrect ? 1 : 0,
    time_seconds: params.timeSeconds,
  });
  if (error) throw error;
}

/**
 * Atualiza a tentativa existente quando a estudante TROCA de alternativa.
 * Sem isto, a 1ª escolha fica registrada e contamina nota/evolução.
 * Não há unique (session_id, question_id) — discursivas avulsas precisam de
 * múltiplos attempts — por isso atualizamos por filtro em vez de upsert.
 */
export async function updateSimAttempt(params: {
  sessionId: string;
  questionId: string;
  selected: string;
  correctAnswer: string | null;
  timeSeconds: number;
}): Promise<void> {
  const isCorrect = params.correctAnswer ? params.selected === params.correctAnswer : null;
  const { error } = await supabase
    .from("usp_attempts")
    .update({
      answer: params.selected,
      is_correct: isCorrect,
      score: isCorrect ? 1 : 0,
      time_seconds: params.timeSeconds,
    })
    .eq("session_id", params.sessionId)
    .eq("question_id", params.questionId);
  if (error) throw error;
}

export async function finishSimulation(sessionId: string): Promise<{
  total: number;
  correct: number;
  durationSeconds: number;
  byDiscipline: Record<string, { total: number; correct: number }>;
}> {
  const { data: attempts, error: aErr } = await supabase
    .from("usp_attempts")
    .select("question_id, is_correct, time_seconds")
    .eq("session_id", sessionId);
  if (aErr || !attempts) throw new Error(`load attempts: ${aErr?.message}`);

  const { data: sess, error: sErr } = await supabase
    .from("usp_sessions")
    .select("started_at, metadata, total_questions")
    .eq("id", sessionId)
    .single();
  if (sErr || !sess) throw new Error(`load session: ${sErr?.message}`);

  const ids = (sess.metadata as { question_ids?: string[] } | null)?.question_ids ?? [];
  const { data: qs } = await supabase
    .from("usp_questions")
    .select("id, discipline")
    .in("id", ids);
  const discByQ = new Map<string, string>(
    ((qs ?? []) as Array<{ id: string; discipline: string }>).map((q) => [q.id, q.discipline]),
  );

  const byDisc: Record<string, { total: number; correct: number }> = {};
  let correct = 0;
  for (const a of attempts as Array<{ question_id: string; is_correct: boolean | null }>) {
    const d = discByQ.get(a.question_id) ?? "unknown";
    const b = byDisc[d] ?? { total: 0, correct: 0 };
    b.total++;
    if (a.is_correct) {
      b.correct++;
      correct++;
    }
    byDisc[d] = b;
  }

  const durationSeconds = Math.floor((Date.now() - new Date(sess.started_at as string).getTime()) / 1000);
  const total = (sess.total_questions as number) ?? attempts.length;

  await supabase
    .from("usp_sessions")
    .update({
      finished_at: new Date().toISOString(),
      duration_seconds: durationSeconds,
      correct_count: correct,
      metadata: { ...(sess.metadata as Record<string, unknown>), by_discipline: byDisc },
    })
    .eq("id", sessionId);

  // Agenda revisão SM-2 para questões erradas.
  try {
    const { data: ownerRow } = await supabase
      .from("usp_sessions")
      .select("user_id")
      .eq("id", sessionId)
      .single();
    const wrongIds = (attempts as Array<{ question_id: string; is_correct: boolean | null }>)
      .filter((a) => a.is_correct === false)
      .map((a) => a.question_id);
    if (ownerRow && wrongIds.length > 0) {
      await scheduleUspReviews({ userId: ownerRow.user_id as string, questionIds: wrongIds });
    }
  } catch (err) {
    console.warn("[usp-sim] scheduleUspReviews failed", err);
  }

  return { total, correct, durationSeconds, byDiscipline: byDisc };
}
